type CapCompareSelectionParams = {
  currentRunIds: string[];
  nextRunIds: string[];
  nextExperimentNames: string[];
  limit: number;
};

type CapCompareSelectionResult = {
  runIds: string[];
  experimentNames: string[];
  isCapped: boolean;
};

type CapAddedEntitiesParams = {
  addedEntities: string[];
  visibleEntities?: string[];
  justHiddenEntities: string[];
  hiddenEntitiesLimit: number;
};

export function capCompareSelection({
  currentRunIds,
  nextRunIds,
  nextExperimentNames,
  limit,
}: CapCompareSelectionParams): CapCompareSelectionResult {
  const currentRunIdsCount = currentRunIds.length;
  const nextRunIdsCount = nextRunIds.length;
  const nextExperimentNamesCount = nextExperimentNames.length;

  // if within limit, keep all selected runs and experiment names
  if (nextRunIdsCount + nextExperimentNamesCount <= limit) {
    return {
      runIds: nextRunIds,
      experimentNames: nextExperimentNames,
      isCapped: false,
    };
  }

  // if there's not enough for all new experiments, keep existing runs and trim experiment names.
  if (currentRunIdsCount + nextExperimentNamesCount > limit) {
    const availableExperimentNamesCount = Math.max(0, limit - currentRunIdsCount);

    return {
      runIds: currentRunIds,
      experimentNames: nextExperimentNames.slice(0, availableExperimentNamesCount),
      isCapped: true,
    };
  }

  const availableRunIdsCount = Math.max(0, limit - nextExperimentNamesCount);

  // Otherwise, keep all experiment names and trim runs to fit the remaining space.
  return {
    runIds: nextRunIds.slice(0, availableRunIdsCount),
    experimentNames: nextExperimentNames,
    isCapped: true,
  };
}

/**
 * Cap hidden compare members because "List only added" includes every added entity in search requests,
 * and large hidden selections make those requests noticeably slower.
 */
export function capAddedEntitiesByHiddenLimit({
  addedEntities,
  visibleEntities = [],
  justHiddenEntities,
  hiddenEntitiesLimit,
}: CapAddedEntitiesParams): { entities: string[]; isCapped: boolean } {
  const visibleEntitiesSet = new Set(visibleEntities);
  const justHiddenEntitiesSet = new Set(justHiddenEntities);
  const hiddenEntities = new Set(addedEntities.filter((entity) => !visibleEntitiesSet.has(entity)));

  if (hiddenEntities.size <= hiddenEntitiesLimit) {
    return { entities: addedEntities, isCapped: false };
  }

  const hiddenEntitiesToRemoveCount = hiddenEntities.size - hiddenEntitiesLimit;
  const hiddenEntitiesToRemove = getHiddenEntitiesToRemove({
    addedEntities,
    hiddenEntities,
    justHiddenEntitiesSet,
    hiddenEntitiesToRemoveCount,
  });

  return {
    entities: addedEntities.filter((entity) => !hiddenEntitiesToRemove.has(entity)),
    isCapped: true,
  };
}

function getHiddenEntitiesToRemove(params: {
  addedEntities: string[];
  hiddenEntities: Set<string>;
  justHiddenEntitiesSet: Set<string>;
  hiddenEntitiesToRemoveCount: number;
}) {
  const { addedEntities, hiddenEntities, justHiddenEntitiesSet, hiddenEntitiesToRemoveCount } =
    params;
  const olderHiddenEntities = addedEntities.filter(
    (entity) => hiddenEntities.has(entity) && !justHiddenEntitiesSet.has(entity),
  );

  if (olderHiddenEntities.length >= hiddenEntitiesToRemoveCount) {
    return new Set(olderHiddenEntities.slice(-hiddenEntitiesToRemoveCount));
  }

  const justHiddenEntitiesToRemoveCount = hiddenEntitiesToRemoveCount - olderHiddenEntities.length;
  const justHiddenEntitiesToRemove = addedEntities
    .filter((entity) => hiddenEntities.has(entity) && justHiddenEntitiesSet.has(entity))
    .slice(-justHiddenEntitiesToRemoveCount);

  return new Set([...olderHiddenEntities, ...justHiddenEntitiesToRemove]);
}
